/**
 * Single source of truth for voice bitrate tiers (Opus, kbps) and the
 * user-facing quality label.
 *
 * The channel creator picks a tier (with the kbps shown) in ChannelModal;
 * listeners see only the label word in the channel header (VoiceChannelStub).
 * Keeping both off this one list avoids the previous drift where three places
 * disagreed on which bitrate counts as "Studio".
 */
import i18n from '@/i18n';
export interface VoiceQualityTier {
  /** Opus bitrate in kbps. */
  value: number;
  /** i18n key of the short label shown to listeners (Standard / High / Studio). */
  labelKey: string;
  /** i18n key of the one-line explanation shown to the channel creator. */
  subtitleKey: string;
}

export const VOICE_QUALITY_TIERS: VoiceQualityTier[] = [
  { value: 64, labelKey: 'lib2.voiceQuality.standard', subtitleKey: 'lib2.voiceQuality.standardHint' },
  { value: 96, labelKey: 'lib2.voiceQuality.high', subtitleKey: 'lib2.voiceQuality.highHint' },
  { value: 128, labelKey: 'lib2.voiceQuality.studio', subtitleKey: 'lib2.voiceQuality.studioHint' },
];

export const DEFAULT_VOICE_BITRATE = 96;

/** Label of the highest tier whose bitrate is ≤ kbps (defaults to the
 *  standard tier for null/undefined). */
export function voiceQualityLabel(kbps: number | null | undefined): string {
  const value = kbps ?? DEFAULT_VOICE_BITRATE;
  let tier = VOICE_QUALITY_TIERS[0]!;
  for (const candidate of VOICE_QUALITY_TIERS) {
    if (value >= candidate.value) tier = candidate;
  }
  return i18n.t(tier.labelKey);
}
